import React from 'react';
import DatePicker, {registerLocale} from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

import {he} from 'date-fns/locale';
import type {Locale} from 'date-fns';

const heCustom: Locale = {
  ...he,
  localize: {
    ...he.localize,
    day: (...args: Parameters<typeof he.localize.day>) =>
      String(he.localize.day(...args)).replace(/^יום\s*/, ''),
  },
};

registerLocale('he', heCustom);

type BaseProps = {
  containerClassName?: string;
  label?: string;
  className?: string;
  px?: string;
  py?: string;
  error?: string;
  labelClassName?: string;
  required?: boolean;
  selected: Date | null;
  onChange: (date: Date | null) => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  dateFormat?: string;
  placeholderText?: string;
};

type Props = BaseProps;

export const DateInput = ({
  containerClassName = '',
  label,
  className = '',
  py = 'py-[10px]',
  error,
  labelClassName,
  required,
  selected,
  onChange,
  disabled,
  icon,
  dateFormat: dateFormatProp,
  placeholderText: placeholderTextProp,
}: Props) => {
  const styles = `
    !w-full
    ${label ? 'pt-[20px] pb-1' : ''}
    ${py}
    pr-10
    pl-3
    border-0
    rounded-[4px]
    border-[1px]
    bg-[#1D1B2014]
    focus:outline-none
    text-[16px] text-black placeholder:text-black
    resize-none
    text-left
    ${className}
  `;

  return (
    <div
      className={`w-full flex flex-col
      ${containerClassName}`}>
      <div className="relative group w-full">
        {label && (
          <span
            className={`text-[12px] font-medium text-[#49454F] absolute top-1 right-2 z-10
              ${error ? 'text-raspberryRed' : 'text-[#49454F]'}
              ${
                error
                  ? 'group-focus-within:text-raspberryRed'
                  : 'group-focus-within:text-tealBlue'
              } 
              ${labelClassName}`}>
            {required && <span className="text-raspberryRed">*</span>}
            {label}
          </span>
        )}

        {!disabled && (
          <div
            className={`absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none z-10 ${label && 'mt-2'}`}>
            {icon ? (
              icon
            ) : (
              <CalendarTodayIcon sx={{fontSize: 16, color: '#333'}} />
            )}
          </div>
        )}

        <DatePicker
          selected={selected}
          onChange={onChange}
          disabled={disabled}
          locale="he"
          dateFormat={dateFormatProp || 'dd/MM/yyyy'}
          placeholderText={placeholderTextProp || 'dd/mm/yyyy'}
          className={styles}
          calendarClassName="rtl-datepicker"
          wrapperClassName="w-full"
          showPopperArrow={false}
          popperPlacement="bottom-end"
          strictParsing
          autoComplete="off"
          showYearDropdown
          showMonthDropdown
          scrollableYearDropdown
          yearDropdownItemNumber={50}
        />
      </div>

      {error && <p className="m-0 text-[12px] text-raspberryRed">{error}</p>}
    </div>
  );
};
